import { Icon, useConfirmContext } from '@sorrem-client/ui/components';
import classnames from 'classnames';
import { useCallback } from 'react';
import { useAppNavigate } from '@sorrem-client/ui/hooks';
import { useCharacterRoster, type CharacterRosterEntry } from '../../hooks';
import styles from './CharacterSelector.module.scss';

interface CharacterSelectorProps {
    /** Called after the player picks a character or starts creating one. */
    onCharacterChosen?: () => void;
    className?: string;
}

/**
 * One slot per existing character plus one empty slot for creating a new
 * character (while the account is under its character limit).
 */
export function CharacterSelector({ onCharacterChosen, className }: CharacterSelectorProps): JSX.Element {
    const navigate = useAppNavigate();
    const { confirm } = useConfirmContext();
    const { entries, canCreateCharacter, isDeletingCharacter, selectCharacter, deleteCharacter } = useCharacterRoster();

    const handleSelect = useCallback(async (characterId: string): Promise<void> => {
        await selectCharacter(characterId);
        onCharacterChosen?.();
    }, [onCharacterChosen, selectCharacter]);

    const handleDelete = useCallback(async (entry: CharacterRosterEntry): Promise<void> => {
        const confirmed = await confirm({
            title: `Delete ${entry.name}?`,
            description: 'This character, their items, and their progress will be permanently lost.',
            confirmLabel: 'Delete',
            cancelLabel: 'Keep',
        });
        if (confirmed) {
            await deleteCharacter(entry.id);
        }
    }, [confirm, deleteCharacter]);

    const handleCreate = useCallback((): void => {
        navigate('/character/create');
        onCharacterChosen?.();
    }, [navigate, onCharacterChosen]);

    return (
        <ul className={classnames(styles.characterSelector, className)}>
            {entries.map((entry) => (
                <li key={entry.id} className={classnames(styles.slot, entry.isSelected && styles.selected)}>
                    {/* Native button: the row is a selectable list entry, not a styled action button. */}
                    <button
                        type="button"
                        className={styles.slotSelect}
                        aria-current={entry.isSelected}
                        onClick={() => void handleSelect(entry.id)}
                    >
                        <span className={styles.slotName}>{entry.name}</span>
                        <span className={styles.slotLevels} title="Veil Attunement : Crafting : Martial Discipline, in percent">
                            ({entry.attunementPercent} : {entry.craftingPercent} : {entry.martialDisciplinePercent})
                        </span>
                    </button>
                    <Icon
                        icon="trash"
                        tooltip={`Delete ${entry.name}`}
                        className={classnames(styles.slotDelete, isDeletingCharacter && styles.disabled)}
                        onClick={isDeletingCharacter ? undefined : () => void handleDelete(entry)}
                    />
                </li>
            ))}

            {canCreateCharacter && (
                <li className={classnames(styles.slot, styles.emptySlot)}>
                    <button type="button" className={styles.slotSelect} onClick={handleCreate}>
                        <span className={styles.slotName}>
                            <Icon icon="plus" />
                            Create new character
                        </span>
                    </button>
                </li>
            )}
        </ul>
    );
}
